import { Badge } from '@/components/ui/badge';
import { useCustodianDrone } from '@/lib/api/queries';
import { makeModelLabel } from '@/lib/droneLabel';
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';
import {
  opsClass,
  selectFleet,
  sourceLabel,
  statusClass,
  useDronesStore,
} from '@/lib/store/drones';
import type { DroneLive } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { useShallow } from 'zustand/shallow';

const tone = (status: TelemetryEnvelope['status'], ops: TelemetryEnvelope['ops_status']) => {
  if (status === 'SA' || status === 'LOST' || ops === 'Non-Normal') return 'crit';
  if (status === 'DAA-DEG' || ops === 'Degraded') return 'warn';
  return 'ok';
};

const tonePxBorder = {
  ok: 'border-t-(--color-green-mid)',
  warn: 'border-t-(--color-amber)',
  crit: 'border-t-(--color-red)',
};

type Cat = 'all' | 'internal' | 'partner' | 'netid';
const CATS: { key: Cat; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'internal', label: 'Internal' },
  { key: 'partner', label: 'Partner' },
  { key: 'netid', label: 'Network ID' },
];

export function FleetList({ onSelect }: { onSelect?: () => void }) {
  const fleet = useDronesStore(useShallow(selectFleet));
  const select = useDronesStore((s) => s.select);
  const cat = useUiStore((s) => s.droneCategory);
  const setCat = useUiStore((s) => s.setDroneCategory);

  const internal = fleet.filter((d) => d.origin === 'internal');
  const partner = fleet.filter((d) => d.origin === 'partner');
  const netid = fleet.filter((d) => d.origin === 'netid');
  const counts: Record<Cat, number> = {
    all: fleet.length,
    internal: internal.length,
    partner: partner.length,
    netid: netid.length,
  };

  // Partner drones bucketed by their `sourceTenant`, tenants alphabetical so
  // the list doesn't reorder as telemetry arrives.
  const partnerTenants = Object.entries(
    partner.reduce<Record<string, DroneLive[]>>((acc, d) => {
      const key = d.partnerTenant ?? 'Unknown tenant';
      (acc[key] ??= []).push(d);
      return acc;
    }, {}),
  ).sort(([a], [b]) => a.localeCompare(b));

  const onPick = (d: DroneLive) => {
    select(d.id);
    onSelect?.();
  };

  const showInternal = cat === 'all' || cat === 'internal';
  const showPartner = cat === 'all' || cat === 'partner';
  const showNetid = cat === 'all' || cat === 'netid';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <div className="text-[0.846em] font-bold">Active Drones</div>
        <div className="text-[0.692em] text-(--color-text3) font-mono">
          {fleet.length} live · 4 Hz
        </div>
        {/* Category filter (mockup drone-filter-row) */}
        <div className="flex gap-1 mt-2 flex-wrap">
          {CATS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCat(c.key)}
              className={cn(
                'px-2 h-6 rounded-full text-[0.615em] font-semibold tracking-wide transition-colors border',
                cat === c.key
                  ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
                  : 'bg-(--color-surface) border-(--color-border) text-(--color-text3) hover:border-(--color-green)',
              )}
            >
              {c.label} <span className="opacity-60">{counts[c.key]}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-2">
        {fleet.length === 0 && (
          <div className="text-[0.769em] text-(--color-text3) text-center py-6">
            Awaiting telemetry…
          </div>
        )}

        {showInternal && internal.length > 0 && (
          <>
            <SectionLabel>Internal</SectionLabel>
            {internal.map((d) => (
              <FleetCard key={d.id} d={d} onPick={onPick} />
            ))}
          </>
        )}

        {showPartner && partner.length > 0 && (
          <>
            <SectionLabel>External</SectionLabel>
            {/* One sub-header per source tenant (mockup
                `_ctInjectPartnerFleetCards`). Partner aircraft arrive on the
                same topics as ours and are told apart only by
                `sourceTenant`, so grouping by it is what makes "whose
                aircraft is this" legible when two partners are active. */}
            {partnerTenants.map(([tenant, drones]) => (
              <div key={tenant}>
                <SectionLabel sub>
                  <span className="inline-flex items-center gap-1.5">
                    <PartnerGlyph />
                    {tenant}
                    <span className="font-mono opacity-60">CT</span>
                  </span>
                </SectionLabel>
                {drones.map((d) => (
                  <FleetCard key={d.id} d={d} onPick={onPick} />
                ))}
              </div>
            ))}
          </>
        )}

        {showNetid && netid.length > 0 && (
          <>
            <SectionLabel sub>
              Network ID <span className="font-mono opacity-60">(ASTM F3411)</span>
            </SectionLabel>
            {netid.map((d) => (
              <FleetCard key={d.id} d={d} onPick={onPick} />
            ))}
          </>
        )}

        {/* Empty-bucket hints when a single category is selected */}
        {cat === 'partner' && partner.length === 0 && <Empty>No partner drones.</Empty>}
        {cat === 'netid' && netid.length === 0 && <Empty>No Network ID broadcasts.</Empty>}
      </div>
    </div>
  );
}

function SectionLabel({ children, sub }: { children: ReactNode; sub?: boolean }) {
  return (
    <div
      className={cn(
        'text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) px-1 pt-2 pb-1',
        sub && 'pl-2',
      )}
    >
      {children}
    </div>
  );
}

/** Grey chevron — the mockup's partner-section marker, same shape as the
 *  map's partner drone icon. */
function PartnerGlyph() {
  return (
    <svg width="8" height="8" viewBox="0 0 44 44" aria-hidden="true" className="shrink-0">
      <polygon points="22,3 40,41 22,33 4,41" fill="currentColor" />
    </svg>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="text-[0.769em] text-(--color-text3) text-center py-4">{children}</div>;
}

function FleetCard({ d, onPick }: { d: DroneLive; onPick: (d: DroneLive) => void }) {
  const t = d.telemetry;
  const external = d.origin !== 'internal';
  const tonality = tone(t.status, t.ops_status);
  const lost = t.status === 'LOST';
  // ENG-271: the feed has no model, so use the aircraft's Custodian record
  // (own fleet only: partner and Network ID aircraft aren't in our inventory).
  const { data: inventory } = useCustodianDrone(external ? null : d.id);
  const makeModel = makeModelLabel(t.drone_model, inventory);
  return (
    <article
      tabIndex={0}
      role="button"
      onClick={() => onPick(d)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onPick(d);
      }}
      className={cn(
        'w-full text-left bg-(--color-surface) rounded-[4px] px-3 py-2.5 mb-2 border border-(--color-border) hover:border-(--color-green) hover:bg-(--color-green-bg) focus:outline-none focus-visible:ring-2 focus-visible:ring-(--color-green) cursor-pointer transition-colors border-t-2',
        external ? 'border-t-(--color-text3)' : tonePxBorder[tonality],
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.923em] font-bold tracking-tight">{t.callsign}</span>
        <div className="flex gap-1">
          {external ? (
            // Partner cards are badged with the owning tenant, not the word
            // "Partner" (mockup `badge-3rdparty`) — the section header above
            // already says these are external; the badge answers "whose".
            <Badge variant="muted" title={d.origin === 'partner' ? 'Partner tenant' : undefined}>
              {d.origin === 'partner' ? (d.partnerTenant ?? 'Partner') : 'Network ID'}
            </Badge>
          ) : (
            <>
              <OpsBadge ops={t.ops_status} />
              <StatusBadge status={t.status} />
            </>
          )}
        </div>
      </div>
      <div className="text-[0.692em] text-(--color-text3) font-mono mt-0.5 flex items-center gap-1.5 flex-wrap">
        {makeModel && (
          <>
            <span>{makeModel}</span>
            <span>·</span>
          </>
        )}
        <span className="text-(--color-text2) font-semibold">{t.drone_type}</span>
        {/* Telemetry-source badge (mockup fi-src) */}
        <span className="ml-auto px-1.5 py-px rounded bg-(--color-bg) text-(--color-text3) text-[0.9em]">
          {sourceLabel(d)}
        </span>
      </div>
      <div className="text-[0.692em] text-(--color-text3) font-mono mt-1 flex items-center gap-1.5 flex-wrap">
        <span>{t.agl != null ? `${Math.round(t.agl)} ft AGL` : '— AGL'}</span>
        <span>·</span>
        <span>
          {t.asl != null ? `${Math.round(t.asl * 3.28084).toLocaleString()} ft ASL` : '— ASL'}
        </span>
        {t.flight_mode && (
          <>
            <span>·</span>
            <span className="text-(--color-blue) font-semibold">{t.flight_mode.toUpperCase()}</span>
          </>
        )}
      </div>
      {lost && (
        <div className="text-[0.692em] text-(--color-red) font-mono mt-0.5 flex items-center gap-1">
          <span aria-hidden>⚠</span>
          <span>Signal lost — no telemetry</span>
        </div>
      )}
      {!lost && t.conflict && (
        <div className="text-[0.692em] text-(--color-red) font-mono mt-0.5 flex items-center gap-1">
          <span aria-hidden>⚠</span>
          <span>Conflict — {t.conflict}</span>
        </div>
      )}
    </article>
  );
}

function OpsBadge({ ops }: { ops: TelemetryEnvelope['ops_status'] }) {
  if (!ops) return null;
  return <Badge variant={opsClass(ops)}>{ops}</Badge>;
}
function StatusBadge({ status }: { status: TelemetryEnvelope['status'] }) {
  if (!status) return null;
  return <Badge variant={statusClass(status)}>{status}</Badge>;
}
